import FooterSetup from '@/features/design-system/components/common/FooterSetup'
import SetUpHeaderModal from '@/features/design-system/components/common/SetUpHeaderModal'
import SetUpLayout from '@/features/design-system/components/common/SetUpLayout'
import TableContainer from '@/features/design-system/components/common/TableContainer'
import { useUnits } from '@/hooks/useUnits'
import { DATA_PAGINATION, SetupStation } from '@/types'
import {
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import _ from 'lodash'
import { memo, useMemo } from 'react'
import classes from './styles.module.scss'
import useTranslation from '@/hooks/useTranslation'

interface Props {
  data?: DATA_PAGINATION<SetupStation>
  onClose: () => void
  onNext: () => void
}

export const StationTable = memo(({ data, onClose, onNext }: Props) => {
  const { t } = useTranslation()
  
  const { convertLabelUnit, convertFromMetric } = useUnits()

  const subTitle = useMemo(() => {
    const groups = _.chain(data?.data || [])
      .groupBy((item) => item.decoderType)
      .map((item, key) => ({ decoderType: key, total: item.length }))
      .value()
      .map((item) => `${item.total} ${item.decoderType}`)
    if (!groups.length) return ''
    return `${groups.join(', ')} decoders`
  }, [data])

  return (
    <SetUpLayout
      title="Review Station Data"
      header={<SetUpHeaderModal title="Station Data" onCancel={onClose} />}
      footer={
        <Stack p="25px 32px">
          <FooterSetup
            title={`${data?.totalCount || 0} rows of data detected`}
            subtitle="Review the data before importing"
          />
        </Stack>
      }
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb="8px"
      >
        <Typography className={classes.title}>Review Station Data</Typography>
        <Stack alignItems="center">
          <Button
            variant="contained"
            className={classes.btnNext}
            onClick={onNext}
          >
            Next
          </Button>
        </Stack>
      </Stack>
      <Typography className={classes.subtitle}>{subTitle}</Typography>
      <Stack flexGrow="1">
        <TableContainer $even={true}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: '29px' }}>ID</TableCell>
                <TableCell sx={{ minWidth: '196px' }}>Decoder Name</TableCell>
                <TableCell sx={{ minWidth: '118.67px' }}>Address</TableCell>
                <TableCell sx={{ minWidth: '68px' }}>Index</TableCell>
                <TableCell sx={{ minWidth: '68px' }}>Type</TableCell>
                <TableCell sx={{ minWidth: '118.67px' }}>Configure</TableCell>
                <TableCell sx={{ minWidth: '119px' }}>Decoder Type</TableCell>
                <TableCell sx={{ minWidth: '59px' }}>Priority</TableCell>
                <TableCell sx={{ minWidth: '103px' }}>Pipe</TableCell>
                <TableCell sx={{ minWidth: '103px' }}>Soil</TableCell>
                <TableCell sx={{ minWidth: '105px' }}>Plant Group</TableCell>
                <TableCell sx={{ minWidth: '118.67px' }}>Emitter</TableCell>
                <TableCell sx={{ minWidth: '118.67px' }}>ETZone</TableCell>
                <TableCell sx={{ minWidth: '118.67px' }}>Area</TableCell>
                <TableCell sx={{ minWidth: '118.67px' }}>Sub-Area</TableCell>
                <TableCell sx={{ minWidth: '121px' }}>
                  Emitter Efficiency
                </TableCell>
                <TableCell sx={{ minWidth: '144px' }}>
                  { t('formsLabelsWaterRequired') } ({convertLabelUnit('mm')})
                </TableCell>
                <TableCell sx={{ minWidth: '175px' }}>
                  Surface Accumulation ({convertLabelUnit('mm')})
                </TableCell>
                <TableCell sx={{ minWidth: '166px' }}>
                  { t('formsLabelsPrecipitationRate') } ({convertLabelUnit('mm/h')})
                </TableCell>
                <TableCell sx={{ minWidth: '175px' }}>
                  Plant Available Water ({convertLabelUnit('mm')})
                </TableCell>
                <TableCell sx={{ minWidth: '138px' }}>
                  { t('formsLabelsExpectedFlow') } ({convertLabelUnit('l/m')})
                </TableCell>
                <TableCell sx={{ minWidth: '135px' }}>
                  Landscape factor (%)
                </TableCell>
                <TableCell sx={{ minWidth: '166px' }}>
                  Runtime day override (min)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.data?.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>{row.controller}</TableCell>
                  <TableCell>{row.address}</TableCell>
                  <TableCell>{row.index}</TableCell>
                  <TableCell>{row.type}</TableCell>
                  <TableCell>{row.configure}</TableCell>
                  <TableCell>{row.decoderType}</TableCell>
                  <TableCell>{row.priority}</TableCell>
                  <TableCell>{row.pipe}</TableCell>
                  <TableCell>{row.soil}</TableCell>
                  <TableCell>{row.plant}</TableCell>
                  <TableCell>{row.emitter}</TableCell>
                  <TableCell>{row?.etZone}</TableCell>
                  <TableCell>{row.areaName}</TableCell>
                  <TableCell>{row.subAreaName}</TableCell>
                  <TableCell>{row.emitterEfficiency}</TableCell>
                  <TableCell>
                    {convertFromMetric(row.waterRequired, 'mm')}
                  </TableCell>
                  <TableCell>
                    {convertFromMetric(row.surfaceAccumulation, 'mm')}
                  </TableCell>
                  <TableCell>
                    {convertFromMetric(row.precipitationRate, 'mm/h')}
                  </TableCell>
                  <TableCell>
                    {convertFromMetric(row.plantAvailWater, 'mm')}
                  </TableCell>
                  <TableCell>
                    {convertFromMetric(row.expectedFlow, 'l/m')}
                  </TableCell>
                  <TableCell>{row.landscapeFactor}</TableCell>
                  <TableCell>{row.rtDayOverride}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </SetUpLayout>
  )
})

export default StationTable
